import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { User } from "../services/api";

interface AuthContextType {
  user: User | null;
  login: () => void;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    user: auth0User,
    loginWithRedirect,
    logout: auth0Logout,
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
  } = useAuth0();

  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    console.log("Auth state changed:", {
      isAuthenticated,
      isLoading,
      auth0User,
    });
    if (isAuthenticated && auth0User) {
      // Convert Auth0 user to our User format
      const convertedUser: User = {
        id: parseInt(auth0User.sub?.split("|")[1] || "0"),
        username: auth0User.nickname || auth0User.name || "User",
        email: auth0User.email || "",
        role: "user", // Default role, could be enhanced with custom claims
      };
      setUser(convertedUser);
      console.log("User set:", convertedUser);
    } else {
      setUser(null);
      console.log("User cleared");
    }
  }, [isAuthenticated, auth0User, isLoading]);

  // Periodic check for token validity (every 5 minutes)
  useEffect(() => {
    if (!isAuthenticated || isLoading) return;

    const checkTokenValidity = async () => {
      try {
        // Try to get a token silently to check if it's still valid
        console.log("Performing periodic token validity check...");
        await getAccessTokenSilently();
        console.log("Token validity check passed");
      } catch (error: any) {
        console.error("Token validity check failed:", error);
        // If token is invalid, clear user state and redirect
        if (
          error.error === "login_required" ||
          error.error === "consent_required" ||
          error.error === "invalid_grant" ||
          error.message?.includes("Login required")
        ) {
          console.log("Token expired during validity check, triggering logout");
          setUser(null);
          // Signal that auth has expired so LoginPage can show appropriate message
          localStorage.setItem("auth_expired", "true");
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }
      }
    };

    // Check immediately and then every 5 minutes
    checkTokenValidity();
    const interval = setInterval(checkTokenValidity, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, isLoading, getAccessTokenSilently]);

  const login = () => {
    loginWithRedirect();
  };

  const logout = () => {
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
    setUser(null);
  };

  const value = {
    user,
    login,
    logout,
    isAuthenticated,
    isAdmin: user?.role === "admin",
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
